import {Col, Form, Input, InputNumber, Radio, Row, Select, Switch} from 'antd';
import {useEffect, useState} from 'react';
import {useMutation} from '@apollo/client';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {UPSERT_NOTIFICATION_BUCKET} from 'graphql/mutations/notificationOrchestrator';
import styles from './workflowOrchestrator.module.scss';
import {isDabaEmail} from 'utils/helper';

const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
const VERIFIER_ROLES = ['USER', 'ADMIN', 'CONTRIBUTOR'];

const BucketForm = ({bucket, onClose, onSaved}) => {
  const [form] = Form.useForm();
  const isEdit = !!bucket;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  const dropPolicy = Form.useWatch('dropPolicy', form);
  const requiresApproval = Form.useWatch('requiresApproval', form);

  useEffect(() => {
    if (bucket) {
      form.setFieldsValue({
        key: bucket.key,
        name: bucket.name,
        description: bucket.description,
        cooldownHours: bucket.cooldownHours ?? 24,
        dropPolicy: bucket.dropPolicy || 'SHIFT_INDEFINITELY',
        dropAfterShiftHours: bucket.dropAfterShiftHours,
        requiresApproval: !!bucket.requiresApproval,
        verifierUserRoles: bucket.verifiers?.userRoles || [],
        defaultApprovers: bucket.defaultApprovers || [],
        active: bucket.active ?? true,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        cooldownHours: 24,
        dropPolicy: 'SHIFT_INDEFINITELY',
        requiresApproval: false,
        active: true,
      });
    }
  }, [bucket, form]);

  const [upsertBucket, {loading}] = useMutation(UPSERT_NOTIFICATION_BUCKET, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const handleFinish = values => {
    setPendingValues(values);
    setConfirmOpen(true);
  };

  const submit = () => {
    if (!pendingValues) return;
    const values = pendingValues;
    const input = {
      key: values.key?.trim(),
      name: values.name?.trim(),
      cooldownHours: Number(values.cooldownHours),
      dropPolicy: values.dropPolicy,
      requiresApproval: !!values.requiresApproval,
      active: !!values.active,
    };

    const description = values.description?.trim();
    if (description) {
      input.description = description;
    }

    if (
      values.dropPolicy === 'DROP_AFTER_MAX_SHIFT' &&
      values.dropAfterShiftHours != null &&
      values.dropAfterShiftHours !== ''
    ) {
      input.dropAfterShiftHours = Number(values.dropAfterShiftHours);
    }

    const verifiers = {};
    if (values.verifierUserRoles?.length) {
      verifiers.userRoles = values.verifierUserRoles;
    }
    if (Object.keys(verifiers).length) {
      input.verifiers = verifiers;
    }

    if (values.defaultApprovers?.length) {
      input.defaultApprovers = values.defaultApprovers;
    }

    upsertBucket({variables: {input}}).then(({data}) => {
      if (!data) return;
      const result = data.upsertNotificationBucket;
      if (result.__typename === 'Error') {
        toast.error(result.message);
      } else {
        toast.success(
          result.message || (isEdit ? 'Bucket updated' : 'Bucket created'),
        );
        setConfirmOpen(false);
        setPendingValues(null);
        onSaved?.();
        onClose?.();
      }
    });
  };

  return (
    <div className={styles.formWrap}>
      <div className={styles.modalHead}>
        <h3 className={styles.modalHeader}>
          {isEdit ? 'Edit Bucket' : 'Create Bucket'}
        </h3>
        <p className={styles.modalSub}>
          Buckets group related notifications and own the rate-limit and
          approval rules.
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        className={styles.modalForm}
        onFinish={handleFinish}
        scrollToFirstError>
        <div className={styles.formSection}>
          <h4 className={styles.sectionHeading}>Identity</h4>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="key"
                label="Bucket key"
                tooltip="Stable identifier. Lowercase, hyphenated. Cannot change after create."
                rules={[
                  {required: true, message: 'Bucket key is required'},
                  {
                    pattern: SLUG_PATTERN,
                    message: 'Use lowercase letters, digits and hyphens only',
                  },
                ]}>
                <Input
                  placeholder="e.g. marketing"
                  disabled={isEdit}
                  className={styles.input}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Display name"
                rules={[{required: true, message: 'Name is required'}]}>
                <Input placeholder="Marketing" className={styles.input} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <Input.TextArea
              rows={2}
              placeholder="One-liner explaining what this bucket holds"
              className={styles.input}
            />
          </Form.Item>
        </div>

        <div className={styles.formSection}>
          <h4 className={styles.sectionHeading}>Rate-limit rules</h4>
          <Row gutter={16}>
            <Col xs={24} md={dropPolicy === 'DROP_AFTER_MAX_SHIFT' ? 12 : 24}>
              <Form.Item
                name="cooldownHours"
                label="Cooldown (hours)"
                tooltip="Minimum hours between two notifications in this bucket for the same user."
                rules={[
                  {required: true, message: 'Cooldown is required'},
                  {
                    type: 'number',
                    min: 1,
                    message: 'Cooldown must be at least 1 hour',
                  },
                ]}>
                <InputNumber min={1} style={{width: '100%'}} />
              </Form.Item>
            </Col>
            {dropPolicy === 'DROP_AFTER_MAX_SHIFT' && (
              <Col xs={24} md={12}>
                <Form.Item
                  name="dropAfterShiftHours"
                  label="Max delivery delay (hours)"
                  rules={[
                    {
                      required: true,
                      message: 'Max delay is required',
                    },
                    ({getFieldValue}) => ({
                      async validator(_, value) {
                        if (value == null) return;
                        const cooldown =
                          Number(getFieldValue('cooldownHours')) || 0;
                        if (Number(value) < cooldown) {
                          throw new Error(
                            'Max delay must be >= cooldown hours',
                          );
                        }
                      },
                    }),
                  ]}>
                  <InputNumber min={1} style={{width: '100%'}} />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Form.Item
            name="dropPolicy"
            label="What to do if the user's queue is busy"
            rules={[{required: true, message: 'Drop policy is required'}]}>
            <Radio.Group className={styles.radioStack}>
              <Radio value="SHIFT_INDEFINITELY">
                <span className={styles.radioLabel}>Shift forever</span>
                <span className={styles.radioHelper}>
                  Always deliver, even if delayed.
                </span>
              </Radio>
              <Radio value="DROP_AFTER_MAX_SHIFT">
                <span className={styles.radioLabel}>Drop after max shift</span>
                <span className={styles.radioHelper}>
                  Give up if it can&apos;t deliver in time.
                </span>
              </Radio>
            </Radio.Group>
          </Form.Item>
        </div>

        <div className={styles.formSection}>
          <h4 className={styles.sectionHeading}>Approval</h4>

          <div className={styles.inlineRow}>
            <div>
              <div className={styles.inlineLabel}>Requires admin approval</div>
              <div className={styles.inlineHelper}>
                When on, notifications start as DRAFT and need approver
                sign-off.
              </div>
            </div>
            <Form.Item
              name="requiresApproval"
              valuePropName="checked"
              style={{marginBottom: 0}}>
              <Switch />
            </Form.Item>
          </div>

          {requiresApproval && (
            <>
              <Form.Item name="verifierUserRoles" label="Approvers — roles">
                <Select
                  mode="multiple"
                  placeholder="Pick roles"
                  style={{width: '100%'}}>
                  {VERIFIER_ROLES.map(role => (
                    <Select.Option key={role} value={role}>
                      {role.charAt(0) + role.slice(1).toLowerCase()}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="defaultApprovers"
                label="Fallback approver emails"
                tooltip="Used if verifiers resolve to nobody. Must be official daba emails."
                rules={[
                  {
                    async validator(_, value) {
                      if (!value || !value.length) return;
                      const invalid = value.filter(
                        email => !isDabaEmail(email),
                      );
                      if (invalid.length) {
                        throw new Error(
                          `Use official daba emails only — invalid: ${invalid.join(
                            ', ',
                          )}`,
                        );
                      }
                    },
                  },
                ]}>
                <Select
                  mode="tags"
                  placeholder="ops@dabafinance.com"
                  tokenSeparators={[',', ' ']}
                  style={{width: '100%'}}
                />
              </Form.Item>

              <div className={styles.warningBox}>
                We recommend filling in at least one approver source (roles or
                fallback emails).
              </div>
            </>
          )}
        </div>

        <div className={styles.formSection}>
          <div className={styles.inlineRow}>
            <div>
              <div className={styles.inlineLabel}>Active</div>
              <div className={styles.inlineHelper}>
                Inactive buckets still block scheduling but are hidden from
                default lists.
              </div>
            </div>
            <Form.Item
              name="active"
              valuePropName="checked"
              style={{marginBottom: 0}}>
              <Switch />
            </Form.Item>
          </div>
        </div>

        <div className={styles.formActions}>
          <SubmitButton
            label={isEdit ? 'Save Changes' : 'Create Bucket'}
            disabled={loading}
          />
        </div>
      </Form>

      <ConfirmModal
        title={isEdit ? 'Save Bucket Changes' : 'Create Bucket'}
        openModal={confirmOpen}
        setOpenModal={setConfirmOpen}
        loading={loading}
        handleOperation={submit}
        confirmText={
          <p className={styles.confirmText}>
            {isEdit
              ? `You're updating bucket "${
                  pendingValues?.name || pendingValues?.key || ''
                }". Its rules will apply to all workflows currently routed through it.`
              : `You're creating bucket "${
                  pendingValues?.name || pendingValues?.key || ''
                }". You can assign workflows to it from the Workflows tab.`}
          </p>
        }
      />
    </div>
  );
};

export default BucketForm;
